// External-content screening (agent-harness lessons §2.5). Text that reaches an agent run from
// outside the run's own instructions (a file, a document, a message someone else wrote, a mail)
// is checked for instruction-shaped content: attempts to override the agent's instructions,
// fake system or authority claims, requests to reveal or move secrets, requests to hide what the
// agent does, and directives hidden in invisible characters, HTML comments or base64.
//
// Modelled on QM's `securityScreen` (yc-software/qm `src/security/security-posture.ts`, MIT):
// three modes, `off` / `observe` / `enforce`, and an enforce that the deployment must allow (QM
// downgrades enforce to observe for its dangerous posture; here the install switch does). QM
// classifies with a model; this is deterministic and cheap instead: patterns and heuristics over
// a bounded, normalised copy of the text, no model call. It flags; it never changes the text.
// A verdict is evidence for a person reviewing the run, not proof: documentation about prompt
// injection is flagged too, and a careful attacker can phrase around patterns.

export const CONTENT_SCREEN_MODES = ["off", "observe", "enforce"] as const;
export type ContentScreenMode = (typeof CONTENT_SCREEN_MODES)[number];

/** A company without a setting is screened in observe mode (record, never block). */
export const DEFAULT_CONTENT_SCREEN_MODE: ContentScreenMode = "observe";

/**
 * The mode that applies: the company's (default observe); `enforce` only where the install
 * allows it, otherwise it records like observe.
 */
export function effectiveScreenMode(
  company: ContentScreenMode | undefined | null,
  enforceAllowed: boolean,
): ContentScreenMode {
  const mode = company ?? DEFAULT_CONTENT_SCREEN_MODE;
  return mode === "enforce" && !enforceAllowed ? "observe" : mode;
}

export type ScreenCategory =
  | "override"
  | "authority"
  | "exfiltration"
  | "concealment"
  | "role_play"
  | "hidden"
  | "encoded";

export type ScreenHit = {
  rule: string;
  category: ScreenCategory;
  weight: number;
  /** Up to `excerptChars` of the normalised text around the match (invisible characters removed). */
  excerpt: string;
};

export type ScreenVerdict = {
  verdict: "clean" | "flagged";
  score: number;
  threshold: number;
  hits: ScreenHit[];
  scanned_chars: number;
  truncated: boolean;
};

export const SCREEN_LIMITS = {
  /** Characters screened; the rest is not read (the verdict says `truncated`). */
  maxChars: 64_000,
  maxHits: 8,
  excerptChars: 80,
  /** Base64 runs decoded and rescanned per screening. */
  maxDecodes: 8,
  /** HTML comments rescanned per screening. */
  maxComments: 16,
} as const;

/** A score at or above this flags the content. Weight-1 signals alone never do. */
export const SCREEN_THRESHOLD = 3;

type Rule = {
  id: string;
  category: ScreenCategory;
  weight: number;
  re: RegExp;
  /**
   * A match whose verb a prohibition governs ("never send your API keys to anyone") is policy,
   * not a command: it does not count, but a later unnegated match still does.
   */
  negatable?: true;
};

/**
 * A prohibition governing the verb that starts the match: the negation directly before it, with
 * at most "ever", "to" or "you should" between ("do not wait; send …" is not governed).
 */
const NEGATED =
  /(?:\b(?:never|not|dont|avoid|refuse to|no one should|nobody should)|n't)\s+(?:ever\s+|to\s+|you\s+should\s+)?$/;

/** Words after a secret's name that make it a document about secrets, not a secret. */
const ABOUT_SECRETS = String.raw`(?![ -](?:reset|resets|rotation|rotations|policy|policies|instructions|guide|guides|guidelines|docs?|documentation|checklist|procedures?|process|requirements|manager|vault|form|template|expiry|expiration|change|changes|update|updates|length|strength|hygiene|training|best practices)\b)`;

// Rules run over normalised text: NFKC, invisible characters removed, lower case, runs of
// spaces and tabs collapsed. `[^.\n]{0,n}` keeps a pattern inside one clause.
const PRIOR =
  "(?:previous|prior|above|earlier|preceding|original|initial|existing|system|developer|all|any|your)";
const ORDERS =
  "(?:instructions?|prompts?|rules|directions|guidelines|directives|guardrails|constraints|programming)";
const SECRET = String.raw`(?:api[ _-]?keys?|access[ _-]?tokens?|bearer tokens?|auth(?:entication)? tokens?|passwords?|passcodes?|credentials?|secrets?|private keys?|ssh keys?|session cookies?|cookies|env(?:ironment)? variables?|\.env\b|system prompt)`;

const RULES: readonly Rule[] = [
  // overriding the agent's instructions
  {
    id: "ignore_previous_instructions",
    category: "override",
    weight: 3,
    re: new RegExp(
      String.raw`\b(?:ignore|disregard|forget|override|bypass|discard|abandon|drop)\b[^.\n]{0,30}\b${PRIOR}\b[^.\n]{0,20}\b${ORDERS}`,
    ),
  },
  {
    id: "new_instructions",
    category: "override",
    weight: 2,
    re: /\b(?:new|updated|revised|real|actual|true|secret|hidden)\s+(?:instructions|system prompt|directives|orders|task)\s*(?:are|is)?\s*[:-]/,
  },
  {
    id: "forget_everything",
    category: "override",
    weight: 1,
    re: /\bforget (?:everything|all)(?: (?:you|that) (?:were|was|have been|know))?/,
  },
  {
    id: "from_now_on",
    category: "override",
    weight: 1,
    re: /\bfrom now on,? (?:you|your)\b[^.\n]{0,10}\b(?:are|will|must|should|shall|only|role)\b/,
  },
  {
    id: "addressed_to_the_agent",
    category: "override",
    weight: 1,
    re: /\b(?:attention|note to|message to|instructions? for|dear)\b[^.\n]{0,15}\b(?:ai|llm|language model|assistant|agent|chatbot|gpt|claude)s?\b[^.\n]{0,5}[:,]/,
  },
  {
    id: "agent_must",
    category: "override",
    weight: 1,
    re: /\b(?:the |any )?(?:ai|llm|language model|assistant|agent|chatbot|model)s? (?:must|shall|is required to|needs to|has to)\b/,
  },
  // role play / jailbreak framing
  {
    id: "you_are_now",
    category: "role_play",
    weight: 2,
    re: /\byou are (?:now|no longer)\b[^.\n]{0,30}\b(?:assistant|ai|model|agent|bot|dan|jailbroken|unrestricted|unfiltered|uncensored|free)\b/,
  },
  { id: "do_anything_now", category: "role_play", weight: 3, re: /\bdo anything now\b/ },
  {
    id: "jailbreak_mode",
    category: "role_play",
    weight: 1,
    re: /\b(?:developer|dan|jailbreak|god|sudo|unrestricted) mode\b/,
  },
  // fake system or authority claims
  {
    id: "fake_system_tag",
    category: "authority",
    weight: 3,
    re: /<\|(?:im_start|im_end|system|endoftext|begin_of_text|start_header_id)\|>|<\/?(?:system|sys|instructions?)>|\[\/?(?:system|inst|sys)\]|<<\/?sys>>/,
  },
  {
    id: "fake_system_header",
    category: "authority",
    weight: 2,
    // `[ \t]*`, not `\s*`: a run of newlines must not be rescanned from every newline in it
    re: /(?:^|\n)[ \t]*(?:#{1,4}[ \t]*)?(?:system|assistant|developer)(?: prompt| message| instructions?)?[ \t]*:[ \t]*\S/,
  },
  {
    id: "prompt_boundary",
    category: "authority",
    weight: 2,
    re: /\b(?:begin|start|end)(?: of)? (?:the )?(?:system|hidden|secret|new|real) (?:prompt|instructions|message)\b/,
  },
  {
    id: "message_from_authority",
    category: "authority",
    weight: 2,
    re: /\b(?:message|note|instruction|notice|directive|order|update)s? from (?:the )?(?:system|administrator|admin|developers?|operator|anthropic|openai|companyos|it department|security team)\b/,
  },
  {
    id: "authority_override_label",
    category: "authority",
    weight: 2,
    re: /\b(?:system|admin|administrator|developer|security|priority|emergency) (?:override|notice|instruction|message|alert|command)\s*[:-]/,
  },
  {
    id: "claimed_authorisation",
    category: "authority",
    weight: 2,
    re: /\b(?:this (?:message|instruction|request|action) (?:is|has been) (?:authori[sz]ed|approved|sanctioned)|the (?:user|owner|admin|administrator) has (?:already )?(?:authori[sz]ed|approved|consented to))\b/,
  },
  {
    id: "claimed_identity",
    category: "authority",
    weight: 2,
    re: /\b(?:i am|this is) (?:your|the) (?:developer|creator|administrator|admin|operator|owner|system|maker)\b/,
  },
  // revealing or moving secrets
  {
    id: "send_secrets",
    category: "exfiltration",
    weight: 3,
    negatable: true,
    re: new RegExp(
      String.raw`\b(?:send|e-?mail|post|upload|forward|transmit|leak|exfiltrate|share|paste|copy)\b[^.\n]{0,60}\b${SECRET}\b${ABOUT_SECRETS}[^.\n]{0,60}\b(?:to|at|into|via|on)\b`,
    ),
  },
  {
    id: "reveal_secrets",
    category: "exfiltration",
    weight: 3,
    negatable: true,
    re: new RegExp(
      String.raw`\b(?:reveal|print|output|show|display|repeat|disclose|dump|list|tell (?:me|us))\b[^.\n]{0,30}\b(?:your|the|all)\b[^.\n]{0,15}\b(?:${SECRET}\b${ABOUT_SECRETS}|hidden instructions|initial instructions|original instructions|instructions above)`,
    ),
  },
  {
    id: "image_beacon",
    category: "exfiltration",
    weight: 3,
    re: /!\[[^\]]{0,100}\]\(https?:\/\/[^)\s]{1,300}[?&][^)\s]{0,200}=(?:\{|\$|%7b|<)/,
  },
  {
    id: "pipe_to_shell",
    category: "exfiltration",
    weight: 2,
    negatable: true,
    re: /\b(?:curl|wget|iwr|invoke-webrequest)\b[^\n]{0,120}\|\s*(?:ba|z|da)?sh\b/,
  },
  // hiding what the agent does
  {
    id: "do_not_tell_the_user",
    category: "concealment",
    weight: 2,
    re: /\b(?:do not|don't|dont|never)\b[^.\n]{0,20}\b(?:tell|inform|mention|reveal|alert|notify|show|let)\b[^.\n]{0,20}\b(?:the user|the human|the operator|the owner|anyone|your (?:user|manager|principal|owner))\b/,
  },
  {
    id: "without_telling",
    category: "concealment",
    weight: 2,
    re: /\bwithout (?:telling|informing|notifying|alerting|asking) (?:the )?(?:user|human|operator|owner|anyone|them)\b/,
  },
  {
    id: "hide_this",
    category: "concealment",
    weight: 1,
    re: /\b(?:hide|conceal|keep) (?:this|these|that) (?:instructions?|message|text)?\s*(?:from|secret)\b/,
  },
  // encoded directives
  {
    id: "decode_and_follow",
    category: "encoded",
    weight: 2,
    re: /\b(?:decode|decipher|base64[- ]?decode|rot13|reverse)\b[^.\n]{0,40}\b(?:and|then)\b[^.\n]{0,10}\b(?:follow|execute|run|obey|do what|carry out)\b/,
  },
];

// Character classes of invisible code points, built from numbers so the source holds none of
// them literally (a formatter or an editor would otherwise turn escapes into invisible text).
const cls = (...points: (number | [number, number])[]) =>
  `[${points
    .map((p) =>
      Array.isArray(p)
        ? `${String.fromCharCode(p[0])}-${String.fromCharCode(p[1])}`
        : String.fromCharCode(p),
    )
    .join("")}]`;
/** Zero-width space/joiners, word joiner and invisible operators, BOM, Mongolian vowel separator, soft hyphen. */
const ZERO_WIDTH = new RegExp(cls([0x200b, 0x200d], [0x2060, 0x2064], 0xfeff, 0x180e, 0xad), "g");
/** Bidirectional embeddings, overrides and isolates. */
const BIDI = new RegExp(cls([0x202a, 0x202e], [0x2066, 0x2069]), "g");
/** Spaces other than the newline: space, tab, form feed, vertical tab, no-break space. */
const SPACES = new RegExp(`${cls(0x20, 0x09, 0x0c, 0x0b, 0xa0)}+`, "g");
/** C0 controls and DEL (newlines included: an excerpt is one line). */
const CONTROLS = new RegExp(`${cls([0x00, 0x1f], 0x7f)}+`, "g");
/** Unicode tag characters (U+E0000–U+E007F): invisible, and U+E0020–U+E007E spell ASCII. */
const TAGS = /[\u{E0000}-\u{E007F}]/gu;
const COMMENT = /<!--([\s\S]{0,4000}?)-->/g;
const BASE64_RUN = /[A-Za-z0-9+/]{40,}={0,2}/g;
const CSS_HIDDEN =
  /\b(?:display\s*:\s*none|visibility\s*:\s*hidden|font-size\s*:\s*0(?:px|em|pt)?\b|opacity\s*:\s*0(?:\.0+)?\b|color\s*:\s*(?:#fff(?:fff)?|white|transparent)\b)/;

/** NFKC, invisible characters out, lower case, spaces collapsed (newlines kept). */
export function normaliseForScreen(text: string): string {
  return text
    .normalize("NFKC")
    .replace(ZERO_WIDTH, "")
    .replace(BIDI, "")
    .replace(TAGS, "")
    .toLowerCase()
    .replace(SPACES, " ");
}

function excerptAt(text: string, index: number, length: number): string {
  const room = Math.max(
    0,
    SCREEN_LIMITS.excerptChars - Math.min(length, SCREEN_LIMITS.excerptChars),
  );
  const start = Math.max(0, index - Math.floor(room / 2));
  return text
    .slice(start, start + SCREEN_LIMITS.excerptChars)
    .replace(CONTROLS, " ")
    .trim();
}

/** Global copies of the negatable rules, to look past a negated match. */
const GLOBAL = new Map(RULES.filter((r) => r.negatable).map((r) => [r.id, new RegExp(r.re, "g")]));

/** The rule's first match that is not right after a prohibition in its clause. */
function firstMatch(r: Rule, text: string): RegExpExecArray | null {
  const g = GLOBAL.get(r.id);
  if (!g) return r.re.exec(text);
  g.lastIndex = 0;
  let checked = 0;
  for (let m = g.exec(text); m; m = g.exec(text)) {
    if (!NEGATED.test(text.slice(Math.max(0, m.index - 40), m.index))) return m;
    if (++checked >= 32) return null; // bounded: a wall of prohibitions is not a command
    g.lastIndex = m.index + 1;
  }
  return null;
}

function matchRules(text: string, prefix: string, into: Map<string, ScreenHit>): void {
  for (const r of RULES) {
    const id = `${prefix}${r.id}`;
    if (into.has(id)) continue;
    const m = firstMatch(r, text);
    if (m)
      into.set(id, {
        rule: id,
        category: r.category,
        weight: r.weight,
        excerpt: excerptAt(text, m.index, m[0].length),
      });
  }
}

/**
 * An emoji subdivision flag (England, Scotland, Wales…): the black flag, 2–6 tag letters or digits,
 * the cancel tag. Ordinary text, not hidden content.
 */
const at = (n: number) => String.fromCodePoint(n);
const SUBDIVISION_FLAG = new RegExp(
  `${at(0x1f3f4)}[${at(0xe0030)}-${at(0xe0039)}${at(0xe0061)}-${at(0xe007a)}]{2,6}${at(0xe007f)}`,
  "gu",
);

/** Hidden text decoded from Unicode tag characters (U+E0020–U+E007E map to ASCII), flags aside. */
function tagText(raw: string): string {
  let out = "";
  for (const ch of raw.replace(SUBDIVISION_FLAG, "").matchAll(TAGS)) {
    const cp = (ch[0].codePointAt(0) as number) - 0xe0000;
    if (cp >= 0x20 && cp <= 0x7e) out += String.fromCharCode(cp);
  }
  return out;
}

function decodeBase64(run: string): string | null {
  try {
    const bin = atob(run.length % 4 === 0 ? run : run.slice(0, run.length - (run.length % 4)));
    let printable = 0;
    for (let i = 0; i < bin.length; i++) {
      const c = bin.charCodeAt(i);
      if ((c >= 0x20 && c < 0x7f) || c === 0x0a || c === 0x0d || c === 0x09) printable++;
    }
    return bin.length > 0 && printable / bin.length >= 0.9 ? bin : null;
  } catch {
    return null;
  }
}

/**
 * Screens one text. Deterministic: the same text always gets the same verdict. Each rule counts
 * once; the score is the sum of the weights of the rules that matched.
 */
export function screenContent(input: string): ScreenVerdict {
  const truncated = input.length > SCREEN_LIMITS.maxChars;
  const raw = truncated ? input.slice(0, SCREEN_LIMITS.maxChars) : input;
  const text = normaliseForScreen(raw);
  const hits = new Map<string, ScreenHit>();
  matchRules(text, "", hits);

  // hidden: Unicode tag characters carry invisible ASCII; decode and rescan them
  const tags = tagText(raw);
  if (tags.length > 0) {
    hits.set("unicode_tags", {
      rule: "unicode_tags",
      category: "hidden",
      weight: 3,
      excerpt: excerptAt(tags, 0, 0),
    });
    matchRules(normaliseForScreen(tags), "hidden:", hits);
  }
  const zeroWidth = raw.match(ZERO_WIDTH)?.length ?? 0;
  if (zeroWidth >= 3)
    hits.set("zero_width", {
      rule: "zero_width",
      category: "hidden",
      weight: 1,
      excerpt: `${zeroWidth} zero-width characters`,
    });
  if (raw.search(BIDI) >= 0)
    hits.set("bidi_control", {
      rule: "bidi_control",
      category: "hidden",
      weight: 1,
      excerpt: "bidirectional control characters",
    });
  // hidden: an HTML comment or CSS-hidden block that carries a directive
  let comments = 0;
  for (const m of text.matchAll(COMMENT)) {
    if (++comments > SCREEN_LIMITS.maxComments) break;
    const inner = new Map<string, ScreenHit>();
    matchRules(m[1] ?? "", "", inner);
    if ([...inner.values()].some((h) => h.weight >= 2)) {
      hits.set("comment_directive", {
        rule: "comment_directive",
        category: "hidden",
        weight: 1,
        excerpt: excerptAt(text, m.index ?? 0, m[0].length),
      });
      break;
    }
  }
  if (
    CSS_HIDDEN.test(text) &&
    [...hits.values()].some((h) => h.weight >= 2 && h.category !== "hidden")
  )
    hits.set("css_hidden_text", {
      rule: "css_hidden_text",
      category: "hidden",
      weight: 1,
      excerpt: "text styled invisible near a directive",
    });
  // encoded: base64 runs that decode to text carrying a directive
  let decodes = 0;
  for (const m of raw.matchAll(BASE64_RUN)) {
    if (++decodes > SCREEN_LIMITS.maxDecodes) break;
    const decoded = decodeBase64(m[0]);
    if (!decoded) continue;
    const inner = new Map<string, ScreenHit>();
    matchRules(normaliseForScreen(decoded), "base64:", inner);
    if (inner.size === 0) continue;
    for (const [k, v] of inner) if (!hits.has(k)) hits.set(k, v);
    hits.set("base64_directive", {
      rule: "base64_directive",
      category: "encoded",
      weight: 1,
      excerpt: excerptAt(m[0], 0, 0),
    });
  }

  const all = [...hits.values()].sort(
    (a, b) => b.weight - a.weight || a.rule.localeCompare(b.rule),
  );
  const score = all.reduce((s, h) => s + h.weight, 0);
  // weak signals add up only behind at least one strong one: three weight-1 hits stay clean
  const strong = all.some((h) => h.weight >= 2);
  return {
    verdict: score >= SCREEN_THRESHOLD && strong ? "flagged" : "clean",
    score,
    threshold: SCREEN_THRESHOLD,
    hits: all.slice(0, SCREEN_LIMITS.maxHits),
    scanned_chars: raw.length,
    truncated,
  };
}

/** Keys whose strings are identifiers, binary payloads or metadata, never screened prose. */
const SKIP_KEYS = new Set([
  "contentBase64",
  "content_base64",
  "data",
  "mimeType",
  "mime_type",
  "createdAt",
  "updatedAt",
  "created_at",
  "updated_at",
]);
const ID_KEY = /(?:^id$|Id$|_id$|Ids$|_ids$)/;

/** Nodes of a JSON value visited at most (numbers, ids and short strings use no character budget). */
export const SCREEN_MAX_NODES = 20_000;

/** How deep into a JSON value the extraction reads. */
export const SCREEN_MAX_DEPTH = 12;

/**
 * The prose of a JSON value (a tool result): every string not under an identifier, binary or
 * timestamp key, joined by newlines, at most `maxChars` characters and `SCREEN_MAX_NODES` nodes.
 * `truncated` says whether anything screenable was left out (a cut string, a field or element
 * past the budget, a level past `SCREEN_MAX_DEPTH`).
 */
export function extractScreenable(
  value: unknown,
  maxChars: number = SCREEN_LIMITS.maxChars,
): { text: string; truncated: boolean } {
  let text = "";
  let nodes = 0;
  let truncated = false;
  const walk = (v: unknown, key: string | null, depth: number): void => {
    if (text.length >= maxChars || nodes >= SCREEN_MAX_NODES) {
      truncated = true;
      return;
    }
    // every value counts, prose or not, and one past the depth limit too (Codex r3 #2): a wall
    // of numbers, or a wide array deep down, is bounded by the node budget
    nodes++;
    if (depth > SCREEN_MAX_DEPTH) {
      truncated = true;
      return;
    }
    if (typeof v !== "string" && (v === null || typeof v !== "object")) return;
    if (typeof v === "string") {
      if (key !== null && (SKIP_KEYS.has(key) || ID_KEY.test(key))) return;
      if (v.length < 2) return;
      const piece = text.length === 0 ? v : `\n${v}`;
      const room = maxChars - text.length;
      if (piece.length > room) truncated = true;
      text += piece.slice(0, room);
    } else if (Array.isArray(v)) {
      for (const x of v) {
        walk(x, key, depth + 1);
        if (truncated && (text.length >= maxChars || nodes >= SCREEN_MAX_NODES)) return;
      }
    } else {
      for (const k in v) {
        if (!Object.hasOwn(v, k)) continue;
        walk((v as Record<string, unknown>)[k], k, depth + 1);
        if (truncated && (text.length >= maxChars || nodes >= SCREEN_MAX_NODES)) return;
      }
    }
  };
  walk(value, null, 0);
  return { text, truncated };
}

/** `extractScreenable`'s text alone. */
export const screenableText = (value: unknown, maxChars?: number): string =>
  extractScreenable(value, maxChars).text;
